import { Button, DatePicker, Form, Input, Modal, Space } from "antd";
import dayjs from "dayjs";

// Quick options to extend the course access. "months" adds to the current date, not to
// the existing course access date.
const COURSE_ACCESS_EXTENSION_OPTIONS = [
  { label: "+3 months", months: 3 },
  { label: "+6 months", months: 6 },
  { label: "Till 30 Sep 2027", date: "2027-09-30T23:59:59" }
];

const getExtendedCourseAccessDate = (option) => {
  if (option.date) {
    return dayjs(option.date);
  }
  return dayjs().add(option.months, "month");
};

const EditSubscriptionConfigModal = ({
  isModalOpen = false,
  initialValues = {},
  setIsModalOpen = () => {},
  handleUpdateSubscriptionConfig = () => {}
}) => {
  const [form] = Form.useForm();

  const {
    doubtSupportAvailableTill,
    courseAccessAvailableTill,
    aiDoubtSupportCreditLimit,
    guidanceSessionsLimit
  } = initialValues;

  // Old subscriptions can have no value for these limits. Show 0, not an empty field.
  const formInitialValues = {
    ...initialValues,
    aiDoubtSupportCreditLimit: aiDoubtSupportCreditLimit ?? 0,
    guidanceSessionsLimit: guidanceSessionsLimit ?? 0,
    doubtSupportAvailableTill: doubtSupportAvailableTill && dayjs(doubtSupportAvailableTill),
    courseAccessAvailableTill: courseAccessAvailableTill && dayjs(courseAccessAvailableTill)
  };

  const handleFormSubmit = (values) => {
    handleUpdateSubscriptionConfig(values);
  };

  const handleExtendCourseAccess = (option) => {
    form.setFieldValue("courseAccessAvailableTill", getExtendedCourseAccessDate(option));
    form.validateFields(["courseAccessAvailableTill"]);
  };

  // A changed course access date must be in the future. The backend revives an expired
  // subscription of an eligible batch only for a future date.
  const validateCourseAccessAvailableTill = (_, value) => {
    const isUnchanged =
      value && courseAccessAvailableTill && dayjs(value).isSame(dayjs(courseAccessAvailableTill));
    if (!value || isUnchanged || dayjs(value).isAfter(dayjs())) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("The new course access date must be in the future!"));
  };

  return (
    <Modal
      title="Edit Config"
      centered
      open={isModalOpen}
      footer={[]}
      onOk={() => {
        setIsModalOpen(false);
      }}
      onCancel={() => setIsModalOpen(false)}
    >
      <Form
        form={form}
        name="edit_subscription_config_form"
        initialValues={formInitialValues}
        onFinish={handleFormSubmit}
      >
        <Form.Item
          name="doubtsLimit"
          label="Doubts Limit"
          rules={[
            {
              required: true,
              message: "Please enter a valid limit!"
            }
          ]}
        >
          <Input placeholder="100" />
        </Form.Item>

        <Form.Item
          name="aiDoubtSupportCreditLimit"
          label="AI Doubt Support Credit Limit"
          rules={[
            {
              required: true,
              message: "Please enter a valid limit!"
            }
          ]}
        >
          <Input placeholder="100" />
        </Form.Item>
        <Form.Item
          name="guidanceSessionsLimit"
          label="Guidance Sessions Limit"
          rules={[
            {
              required: true,
              message: "Please enter a valid limit!"
            }
          ]}
        >
          <Input placeholder="10" />
        </Form.Item>
        <Form.Item
          name="doubtSupportAvailableTill"
          label="Doubt Support Available Till"
          rules={[
            {
              required: true,
              message: "Please select the doubt support end date!"
            }
          ]}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
        </Form.Item>
        <Form.Item
          name="courseAccessAvailableTill"
          label="Course Access Available Till"
          rules={[
            {
              required: true,
              message: "Please select the course access end date!"
            },
            { validator: validateCourseAccessAvailableTill }
          ]}
          extra={
            <Space size={[8, 8]} wrap style={{ marginTop: 8 }}>
              {COURSE_ACCESS_EXTENSION_OPTIONS.map((option) => (
                <Button
                  key={option.label}
                  size="small"
                  shape="round"
                  onClick={() => handleExtendCourseAccess(option)}
                >
                  {option.label}
                </Button>
              ))}
            </Space>
          }
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
          />
        </Form.Item>
        <Form.Item className="submit-btn">
          <Button type="primary" htmlType="submit">
            Update Config
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditSubscriptionConfigModal;
